import { translate } from '@thoughtspot/i18n';
import { AnswerColumn } from '/@services/generated/graphql-types';
import {
    AxisItem,
    AxisItemType,
    DualAxisItem,
    FlatAxisItem,
    MergedAxisItem,
} from '../../column-configure.util';

/**
 * Type guards for runtime type checking
 */
export const isFlat = (item: AxisItem): item is FlatAxisItem =>
    item.type === AxisItemType.FLAT;
export const isMerged = (item: AxisItem): item is MergedAxisItem =>
    item.type === AxisItemType.MERGED;
export const isDual = (item: AxisItem): item is DualAxisItem =>
    item.type === AxisItemType.DUAL;

/**
 * Helper to get all flat items from any item type
 */
export function getFlatItems(item: AxisItem): FlatAxisItem[] {
    if (isFlat(item)) {
        return [item];
    }

    if (isMerged(item)) {
        return item.children;
    }

    if (isDual(item)) {
        return [...item.primary.children, ...item.secondary.children];
    }

    return [];
}

/**
 * Item factory for creating new items
 */
export class AxisItemFactory {
    private static itemCounter = 0;

    /**
     * Create a flat item
     */
    static createFlat(column: AnswerColumn): FlatAxisItem {
        return {
            id: `flat-${column.id}-${this.itemCounter++}`,
            type: AxisItemType.FLAT,
            column,
        };
    }

    /**
     * Create a merged item from flat items
     */
    static createMerged(
        title: string,
        children: FlatAxisItem[],
    ): MergedAxisItem {
        return {
            id: `merged-${title}-${children
                .map(child => child.id)
                .join('-')}-${this.itemCounter++}`,
            title,
            type: AxisItemType.MERGED,
            children,
        };
    }

    /**
     * Create a dual item from two merged items
     */
    static createDual(
        title: string,
        primary: MergedAxisItem,
        secondary: MergedAxisItem,
    ): DualAxisItem {
        return {
            id: `dual-${title}-${primary.id}-${secondary.id}-${this
                .itemCounter++}`,
            title,
            type: AxisItemType.DUAL,
            primary,
            secondary,
        };
    }

    /**
     * Convert merged item to dual item
     * Splits children between primary and secondary
     */
    static mergeToDual(merged: MergedAxisItem): DualAxisItem {
        // Put all except the last column in primary, and only the last column in secondary
        const lastIndex = merged.children.length - 1;
        const mergedAxisTitle = translate(
            'chart.chartEditor.chartAxisConfigurator.MERGED_AXIS_TITLE',
        );

        return this.createDual(
            merged.title,
            this.createMerged(
                mergedAxisTitle,
                merged.children.slice(0, lastIndex),
            ),
            this.createMerged(
                mergedAxisTitle,
                merged.children.slice(lastIndex),
            ),
        );
    }

    /**
     * Convert dual item to merged item
     * Combines primary and secondary children
     */
    static dualToMerged(dual: DualAxisItem): MergedAxisItem {
        return this.createMerged(dual.title, [
            ...dual.primary.children,
            ...dual.secondary.children,
        ]);
    }

    /**
     * Ungroup merged item to flat items
     */
    static ungroupMerged(merged: MergedAxisItem): FlatAxisItem[] {
        return merged.children;
    }

    /**
     * Ungroup dual item to flat items
     */
    static ungroupDual(dual: DualAxisItem): FlatAxisItem[] {
        return [...dual.primary.children, ...dual.secondary.children];
    }
}
